import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getConsent,
  saveConsent,
  applyStoredConsent,
  OPEN_SETTINGS_EVENT,
} from "../../lib/cookieConsent";

// Bottom-bar cookie consent (Option A). Non-essential cookies (Google
// Analytics via GTM) load ONLY after the visitor accepts — "Reject all" is as
// easy as "Accept all", per UK/EU rules. A footer link reopens this to change.
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Load analytics now if already allowed; show the bar if never decided.
    const stored = applyStoredConsent();
    if (!stored) setVisible(true);

    const reopen = () => {
      const current = getConsent();
      setAnalytics(Boolean(current?.analytics));
      setMarketing(Boolean(current?.marketing));
      setManaging(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, reopen);
  }, []);

  if (!visible) return null;

  const decide = (a, m) => {
    saveConsent({ analytics: a, marketing: m });
    setVisible(false);
    setManaging(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="mx-auto max-w-5xl rounded-xl border-t-4 border-[#f5c45e] bg-[#3d0000] p-4 shadow-2xl sm:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="md:flex-1">
            <p className="text-sm font-semibold text-white">We value your privacy</p>
            <p className="mt-1 text-[13px] leading-relaxed text-[#e8d9bd]">
              We use essential cookies to run the site and, only with your consent, analytics
              cookies (Google Analytics) to understand how the site is used.{" "}
              <Link to="/cookie-policy" className="text-[#f5c45e] underline-offset-2 hover:underline">
                Cookie Policy
              </Link>
            </p>
          </div>

          {!managing && (
            <div className="flex flex-wrap gap-2 md:flex-nowrap">
              <button
                type="button"
                onClick={() => decide(false, false)}
                className="rounded-md border border-[#f5c45e] px-4 py-2 text-[13px] font-medium text-[#f5c45e] transition hover:bg-white/5"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={() => {
                  const c = getConsent();
                  setAnalytics(Boolean(c?.analytics));
                  setMarketing(Boolean(c?.marketing));
                  setManaging(true);
                }}
                className="rounded-md px-3 py-2 text-[13px] text-[#e8d9bd] transition hover:text-white"
              >
                Manage
              </button>
              <button
                type="button"
                onClick={() => decide(true, true)}
                className="rounded-md bg-[#f5c45e] px-5 py-2 text-[13px] font-semibold text-[#3d0000] transition hover:bg-[#ffd784]"
              >
                Accept all
              </button>
            </div>
          )}
        </div>

        {managing && (
          <div className="mt-4 border-t border-white/15 pt-4">
            <Row label="Essential" hint="Always on — required for the site to work." locked />
            <Row
              label="Analytics"
              hint="Google Analytics — helps us improve the site."
              checked={analytics}
              onChange={setAnalytics}
            />
            <Row
              label="Marketing / data sharing"
              hint="Cookies that share data with partners."
              checked={marketing}
              onChange={setMarketing}
            />
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => decide(false, false)}
                className="rounded-md border border-[#f5c45e] px-4 py-2 text-[13px] text-[#f5c45e] transition hover:bg-white/5"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={() => decide(analytics, marketing)}
                className="rounded-md bg-[#f5c45e] px-5 py-2 text-[13px] font-semibold text-[#3d0000] transition hover:bg-[#ffd784]"
              >
                Save choices
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, hint, checked, onChange, locked = false }) {
  return (
    <div className="flex items-center justify-between border-t border-white/10 py-2.5 first:border-t-0">
      <div className="pr-3">
        <p className="text-[13px] text-white">{label}</p>
        <p className="text-[12px] text-[#c9b79a]">{hint}</p>
      </div>
      {locked ? (
        <span className="text-[12px] text-[#c9a24b]">Required</span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={label}
          onClick={() => onChange(!checked)}
          className={`relative h-5 w-9 shrink-0 rounded-full transition ${
            checked ? "bg-[#c9a24b]" : "bg-[#6b5545]"
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
              checked ? "right-0.5" : "left-0.5"
            }`}
          />
        </button>
      )}
    </div>
  );
}
